import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import { motion } from 'framer-motion';

const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Upload', path: '/upload' },
];

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ height: 70 }}>
                        {/* Logo */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Box
                                component={Link}
                                to="/"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                    }}
                                >
                                    <MapIcon sx={{ color: 'white', fontSize: 24 }} />
                                </Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    GIS Analysis
                                </Typography>
                            </Box>
                        </motion.div>

                        <Box sx={{ flexGrow: 1 }} />

                        {/* Desktop Navigation */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {navItems.map((item, index) => (
                                    <motion.div
                                        key={item.path}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                    >
                                        <Button
                                            component={Link}
                                            to={item.path}
                                            sx={{
                                                color: isActive(item.path) ? 'primary.main' : 'text.primary',
                                                fontWeight: isActive(item.path) ? 600 : 400,
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: isActive(item.path) ? '60%' : 0,
                                                    height: 2,
                                                    backgroundColor: 'primary.main',
                                                    borderRadius: 1,
                                                    transition: 'width 0.2s ease',
                                                },
                                                '&:hover::after': {
                                                    width: '60%',
                                                },
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    </motion.div>
                                ))}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                >
                                    <Button
                                        component={Link}
                                        to="/upload"
                                        variant="contained"
                                        size="medium"
                                        sx={{
                                            ml: 2,
                                            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                        }}
                                    >
                                        Start Analysis
                                    </Button>
                                </motion.div>
                            </Box>
                        )}

                        {/* Mobile Menu Button */}
                        {isMobile && (
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleDrawerToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                PaperProps={{
                    sx: {
                        width: 280,
                        backgroundColor: 'background.paper',
                    },
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleDrawerToggle}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <List>
                    {navItems.map((item) => (
                        <ListItem key={item.path} disablePadding>
                            <ListItemButton
                                component={Link}
                                to={item.path}
                                onClick={handleDrawerToggle}
                                selected={isActive(item.path)}
                            >
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: isActive(item.path) ? 600 : 400,
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    <ListItem sx={{ mt: 2, px: 2 }}>
                        <Button
                            component={Link}
                            to="/upload"
                            variant="contained"
                            fullWidth
                            onClick={handleDrawerToggle}
                            sx={{
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                            }}
                        >
                            Start Analysis
                        </Button>
                    </ListItem>
                </List>
            </Drawer>
        </>
    );
};

export default Header;
